import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import UnsavedChangesPopup from '../Components/UnsavedChangesPopup';

const UnsavedChangesContext = createContext();

// Holds whether the current page has unsaved changes and asks the user before they leave it:
// in-app navigation (links, navigate(), browser Back/Forward) shows a confirm popup, while
// closing or refreshing the tab gets the browser's own prompt. Pages report their state with
// the hooks in hooks/useUnsavedChanges.js. Actions that leave without navigating, like signing
// out, go through confirmLeave().
const UnsavedChangesProvider = ({ children }) => {
  // Navigation checks run outside React's render cycle (e.g. navigate() right after a save),
  // so they read this ref, which setDirty updates immediately.
  const isDirtyRef = useRef(false);
  const [pendingAction, setPendingAction] = useState(null);

  const setDirty = useCallback((dirty) => {
    isDirtyRef.current = dirty;
  }, []);

  // Runs `action` right away, or after the user confirms if there are unsaved changes.
  const confirmLeave = useCallback((action) => {
    if (isDirtyRef.current) {
      setPendingAction(() => action);
    } else {
      action();
    }
  }, []);

  // Moving within the same page (e.g. a query string change) does not lose the form.
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname,
    []
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleStay = () => {
    blocker.reset?.();
    setPendingAction(null);
  };

  const handleLeave = () => {
    setDirty(false);
    // proceed exists only while a navigation is being held back
    blocker.proceed?.();
    pendingAction?.();
    setPendingAction(null);
  };

  // Must stay the same object across renders: AuthProvider (and so every page's `logout`)
  // consumes this, and pages refetch when `logout` changes. A new value each time the popup
  // opens would re-render them and wipe the form the user chose to stay on.
  const value = useMemo(() => ({ setDirty, confirmLeave }), [setDirty, confirmLeave]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {(blocker.state === 'blocked' || pendingAction) && (
        <UnsavedChangesPopup onStay={handleStay} onLeave={handleLeave} />
      )}
    </UnsavedChangesContext.Provider>
  );
};

export { UnsavedChangesProvider };

export default UnsavedChangesContext;
